<template>
  <lf-dropdown
    v-on-click-outside="onClickOutside"
    name="add-field-dropdown"
    class="h-6 flex-none"
    :options="fieldOptions"
    :toggle-from-parent="showAddFieldDropdown"
    search-enabled
    menu-width="fit-content"
    :teleport-options="{
      enabled: true,
      horizontalScrollable: 'offer-fields-modal',
    }"
    custom-z-index="z-999"
    @menu:toggle="toggleMenu"
    @change="handleAddField"
  >
    <template #toggle-button="{ toggle }">
      <div
        data-cy="add-field-button"
        class="flex w-24 cursor-pointer items-center space-x-1 text-blue-600"
        @click.stop="toggle"
      >
        <icon-base
          :icon="IconPlusBlue"
          class="-mt-2.5"
          height="30"
          width="30"
          view-box="0 0 60 60"
        />
        {{ $t("COMMON.ADD") }} {{ $t("COMMON.FIELD") }}
      </div>
    </template>
  </lf-dropdown>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import cloneDeep from "lodash/cloneDeep";
import { v4 as generateUuid } from "uuid";

import { BpOfferSection } from "@/enums/offerTemplates";
import { FieldType } from "@/enums/offers";

import IconPlusBlue from "@/components/icons/IconPlusBlue.vue";
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";

import type { OfferTemplateField } from "@/models/offers";

interface FieldOptions {
  [key: string]: Omit<OfferTemplateField, "id">;
}

const props = defineProps<{
  availableFields: Record<string, OfferTemplateField>;
}>();

const emit = defineEmits<{
  addField: [OfferTemplateField];
}>();

const { t } = useI18n();

const showAddFieldDropdown = ref(false);

const createDefaultFieldProperties = () => ({
  visibility: {
    workflow_live: true,
    borrower_platform: true,
  },
  borrower_platform: {
    weight: 0,
    section: BpOfferSection.EXPANDED,
  },
});

const createCustomField = (): Omit<OfferTemplateField, "id"> => ({
  name: "custom",
  type: FieldType.INTEGER,
  foreign_via: null,
  rules: [],
  offer: {
    label: t("COMMON.CUSTOM_FIELD"),
    ...createDefaultFieldProperties(),
  },
  closing: {
    label: t("COMMON.CUSTOM_FIELD"),
    ...createDefaultFieldProperties(),
  },
});

const significantFields = computed<FieldOptions>(() => ({
  ...props.availableFields,
  custom: createCustomField(),
}));

const fieldOptions = computed(() => {
  const options = Object.values(significantFields.value).reduce(
    (acc: Record<string, string>, field) => {
      acc[field.name] = field.offer.label as string;
      return acc;
    },
    {}
  );
  return { custom: t("COMMON.CUSTOM_FIELD"), ...options };
});

const toggleMenu = () => {
  showAddFieldDropdown.value = !showAddFieldDropdown.value;
};

const onClickOutside = (event: MouseEvent) => {
  if ((event.target as HTMLElement).role?.includes("search")) {
    return;
  }
  showAddFieldDropdown.value = false;
};

const handleAddField = (fieldName: string | number) => {
  const fieldTemplate = significantFields.value[fieldName as string];
  if (!fieldTemplate) {
    return;
  }

  const fieldId = generateUuid();
  const newField: OfferTemplateField = {
    ...cloneDeep(fieldTemplate),
    id: fieldId,
    offer: {
      ...fieldTemplate.offer,
      ...createDefaultFieldProperties(),
    },
    closing: {
      ...fieldTemplate.closing,
      ...createDefaultFieldProperties(),
    },
  };

  if (fieldName === "custom") {
    newField.name = `custom_${fieldId}`;
  }

  emit("addField", newField);
  toggleMenu();
};
</script>
